import { useThreeContext } from "@/components/common/three-context/three-context";
import { useCallback } from "react";

/**
 * Custom React hook that returns a function to capture a screenshot at the annotation creation time.
 *
 * @throws if the renderer is not available or if the image blob creation fails.
 * @returns A callback function that, when invoked, captures a screenshot and returns a PNG `File`.
 */
export function useAnnotationScreenshot(): () => Promise<File> {
  const { renderer } = useThreeContext();

  return useCallback(async () => {
    if (!renderer) {
      throw new Error("Renderer not available");
    }

    // wait for the image to be fully rendered
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve());
    });

    async function takeScreenshot(canvas: HTMLCanvasElement): Promise<File> {
      const imageBlob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob(resolve),
      );

      if (!imageBlob) {
        throw new Error("Failed to create image blob");
      }

      return new File([imageBlob], `snapshot_${Date.now()}.png`, {
        type: "image/png",
      });
    }

    return await takeScreenshot(renderer.domElement);
  }, [renderer]);
}
